import Icon from 'components/_UI/Icon';
import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Column } from 'styled/alignment/Column';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import DashHeader from './DashHeader';
import DashHeaderActions from './DashHeaderActions';
import paths from 'routes/paths';
import { Link } from 'react-router-dom';
import Text from 'components/_UI/Text';
import { useUser } from 'context/user/User';
import { useOnSelectMenu } from 'context/general/OnSelectMenu';
import roles from 'helpers/roles';
import { BarChart2, Home, Calendar, Package, DollarSign, ShoppingBag, Monitor, CheckSquare, HelpCircle, User, Clipboard, Clock, ChevronDown, ChevronRight, Coffee } from 'lucide-react';
import DynamicProfileImage from 'components/commons/DynamicProfileImage';

const featherIcons = {
    'chart': BarChart2,
    'store': Home,
    'cheers': Calendar,
    'label': Package,
    'money': DollarSign,
    'orders': ShoppingBag,
    'twoPageStore': Monitor,
    'qrcode': CheckSquare,
    'support': HelpCircle,
    'profile': User,
    'task': Clipboard,
    'restaurant': Coffee,
};

export default function DashSlot({ children }) {
    const { user } = useUser();
    const { isLateralMenuActive, setIsLateralMenuActive } = useOnSelectMenu();

    const [openSubMenus, setOpenSubMenus] = useState({});

    function toggleSubMenu(key) {
        setOpenSubMenus(prev => ({ ...prev, [key]: !prev[key] }));
    }

    const mainNavItems = [
        { name: 'Dashboard', icon: 'chart', route: paths.dashboard.statistics },
        { name: 'Perfil', icon: 'profile', route: paths.dashboard.profile, roles: [roles.PRODUCER, roles.ESTABLISHMENT, roles.ADMIN] },
        { name: 'Eventos', icon: 'cheers', route: paths.dashboard.events },
        { name: 'Produtos', icon: 'label', route: paths.dashboard.products },
    ];

    const managementNavItems = [
        { name: 'Financeiro', icon: 'money', route: paths.dashboard.financial },
        { name: 'Pedidos', icon: 'orders', route: paths.dashboard.orders },
        { name: 'PDV', icon: 'twoPageStore', route: paths.dashboard.orders_establishment },
        {
            name: 'Estabelecimento',
            icon: 'restaurant',
            roles: [roles.ESTABLISHMENT, roles.ADMIN],
            subMenu: [
                { name: 'Cardápio', route: paths.dashboard.restaurant.menu },
                { name: 'Pedidos', route: paths.dashboard.restaurant.kitchen },
                { name: 'Comandas', route: paths.dashboard.restaurant.waiter },
                { name: 'Caixa', route: paths.dashboard.restaurant.cashier },
                { name: 'Administração', route: paths.dashboard.restaurant.admin },
                { name: 'Estatísticas', route: paths.dashboard.restaurant.statistics },
            ]
        },
    ];

    const toolNavItems = [
        { name: 'Validação', icon: 'qrcode', route: paths.dashboard.checkin },
        { name: 'Suporte', icon: 'support', route: paths.dashboard.support },
    ];

    const comingSoonItems = [
        { name: 'Tarefas', icon: 'task', roles: [roles.PRODUCER, roles.ESTABLISHMENT, roles.ADMIN] },
    ];

    function shouldShowItem(item) {
        if (item.roleOnly && user?.role?.id != item.roleOnly) return false;
        if (item.roles && !item.roles.some(r => user?.role?.id == r)) return false;
        return true;
    }

    function shouldShowComingSoon(item) {
        return item.roles.some(r => user?.role?.id == r);
    }

    const showComingSoonSection = (
        user?.role?.id == roles?.PRODUCER ||
        user?.role?.id == roles?.ESTABLISHMENT ||
        user?.role?.id == roles?.ADMIN
    );

    function renderNavItem(item) {
        if (!shouldShowItem(item)) return null;
        const FeatherIcon = featherIcons[item.icon];

        if (item.subMenu) {
            const isOpen = openSubMenus[item.name];
            const isAnyChildSelected = item.subMenu.some(child => window.location.pathname === child.route);

            return (
                <React.Fragment key={item.name}>
                    <SubMenuToggle
                        selected={isAnyChildSelected}
                        onClick={() => toggleSubMenu(item.name)}
                    >
                        <NavIconWrapper selected={isAnyChildSelected}>
                            {FeatherIcon && <FeatherIcon size={18} />}
                        </NavIconWrapper>
                        <NavLabel>{item.name}</NavLabel>
                        {isOpen
                            ? <ChevronDown size={14} style={{ marginLeft: 'auto' }} />
                            : <ChevronRight size={14} style={{ marginLeft: 'auto' }} />
                        }
                    </SubMenuToggle>
                    {isOpen && (
                        <SubMenuList>
                            {item.subMenu.filter(child => !child.roleOnly || user?.role?.id == child.roleOnly).map(child => {
                                const isSelected = window.location.pathname === child.route;
                                return (
                                    <SubMenuItem key={child.route} to={child.route} selected={isSelected}>
                                        <NavLabel>{child.name}</NavLabel>
                                    </SubMenuItem>
                                );
                            })}
                        </SubMenuList>
                    )}
                </React.Fragment>
            );
        }

        const isSelected = window.location.pathname === item.route;

        return (
            <NavItem key={item.route} to={item.route} selected={isSelected}>
                <NavIconWrapper selected={isSelected}>
                    {FeatherIcon
                        ? <FeatherIcon size={18} />
                        : (
                            <IconLegacyWrapper>
                                <Icon
                                    name={item.icon}
                                    color={isSelected ? colors.occasionalPurple : colors.night}
                                />
                            </IconLegacyWrapper>
                        )
                    }
                </NavIconWrapper>
                <NavLabel>{item.name}</NavLabel>
            </NavItem>
        );
    }

    function closeMobileMenu() {
        setIsLateralMenuActive(false);
    }

    return (
        <Container>
            <DashHeader visible />
            <DashHeaderActions />

            {isLateralMenuActive && <MobileOverlay onClick={closeMobileMenu} />}

            <Sidebar active={isLateralMenuActive}>
                <SidebarInner>
                    <NavSection>
                        <SectionTitle>Principal</SectionTitle>
                        {mainNavItems.map(renderNavItem)}
                    </NavSection>

                    <NavDivider />

                    <NavSection>
                        <SectionTitle>Gestão</SectionTitle>
                        {managementNavItems.map(renderNavItem)}
                    </NavSection>

                    <NavDivider />

                    <NavSection>
                        <SectionTitle>Ferramentas</SectionTitle>
                        {toolNavItems.map(renderNavItem)}
                    </NavSection>

                    {showComingSoonSection && (
                        <>
                            <NavDivider />
                            <NavSection>
                                <SectionTitle>
                                    Em breve
                                    <ComingSoonBadge>
                                        <Clock size={10} />
                                        Novidades
                                    </ComingSoonBadge>
                                </SectionTitle>
                                {comingSoonItems.map(item => {
                                    if (!shouldShowComingSoon(item)) return null;
                                    const FeatherIcon = featherIcons[item.icon];
                                    return (
                                        <NavItemDisabled key={item.name}>
                                            <NavIconWrapper disabled>
                                                {FeatherIcon
                                                    ? <FeatherIcon size={18} />
                                                    : (
                                                        <IconLegacyWrapper>
                                                            <Icon name={item.icon} color={colors.night + '55'} />
                                                        </IconLegacyWrapper>
                                                    )
                                                }
                                            </NavIconWrapper>
                                            <NavLabel>{item.name}</NavLabel>
                                        </NavItemDisabled>
                                    );
                                })}
                            </NavSection>
                        </>
                    )}
                </SidebarInner>
            </Sidebar>

            <MainContent>
                <Content>
                    {children}
                </Content>
            </MainContent>
        </Container>
    )
}

const Container = styled.div`
    height: auto;
    width: 100%;
    flex-direction: column;

    @media screen and (min-width: ${breakpoints.sm}) {
        display: flex;
        width: 100%;
        flex-direction: row;
    }
`;

const MobileOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 30;
    backdrop-filter: blur(2px);

    @media screen and (min-width: ${breakpoints.lg}) {
        display: none;
    }
`;

const Sidebar = styled.nav`
    background: #fff;
    width: 0;
    opacity: 0;
    height: 100vh;
    display: flex;
    flex-direction: column;
    padding-top: 100px;
    overflow: hidden;
    position: fixed;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    left: 0;
    top: 0;
    z-index: 31;
    border-right: 1.5px solid ${colors.night}15;

    @media screen and (min-width: ${breakpoints.lg}) {
        opacity: 1 !important;
        width: 240px;
        padding-top: 110px;
        box-shadow: 0 1px 8px ${colors.night}08;
    }

    ${({ active }) => active && `
        width: 280px;
        opacity: 1;
        box-shadow: 4px 0 24px ${colors.night}12;
    `}
`;

const SidebarInner = styled.div`
    display: flex;
    flex-direction: column;
    padding: 20px 14px;
    overflow-y: auto;
    flex: 1;

    &::-webkit-scrollbar {
        width: 3px;
    }

    &::-webkit-scrollbar-thumb {
        background: ${colors.night}22;
        border-radius: 10px;
    }
`;

const NavSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const SectionTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 700;
    color: ${colors.night}99;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 0 14px;
    margin-bottom: 6px;
`;

const ComingSoonBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 9px;
    font-weight: 700;
    color: ${colors.occasionalPurple};
    background: ${colors.occasionalPurple}10;
    padding: 2px 6px;
    border-radius: 8px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
`;

const NavDivider = styled.div`
    height: 1px;
    background: ${colors.night}11;
    margin: 12px 14px;
`;

const NavItem = styled(Link).withConfig({
    shouldForwardProp: (prop) => !['selected'].includes(prop),
})`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 14px;
    border-radius: 10px;
    cursor: pointer;
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;
    color: ${({ selected }) => selected ? colors.occasionalPurple : colors.nightDark};
    background: ${({ selected }) => selected ? colors.occasionalPurple + '0A' : 'transparent'};
    transition: all 0.15s ease;
    position: relative;

    ${({ selected }) => selected && `
        font-weight: 600;
        // box-shadow: inset 3px 0 0 ${colors.occasionalPurple};
    `}

    &:hover {
        background: ${({ selected }) => selected ? colors.occasionalPurple + '0F' : colors.night + '08'};
        color: ${({ selected }) => selected ? colors.occasionalPurple : colors.nightDark};
    }
`;

const NavItemDisabled = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 14px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 500;
    color: ${colors.night}55;
    cursor: default;
    opacity: 0.6;
`;

const NavIconWrapper = styled.div.withConfig({
    shouldForwardProp: (prop) => !['selected', 'disabled'].includes(prop),
})`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: ${({ selected, disabled }) =>
        disabled ? colors.night + '08'
            : selected ? colors.occasionalPurple + '15'
                : colors.night + '08'};
    color: ${({ selected, disabled }) =>
        disabled ? colors.night + '55'
            : selected ? colors.occasionalPurple
                : colors.night};
    transition: all 0.15s ease;
`;

const IconLegacyWrapper = styled.div`
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const NavLabel = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const MainContent = styled.main`
    width: 100%;
    padding-top: 120px;
    min-height: 100vh;
    background: ${colors.neutralBackground};

    @media screen and (min-width: ${breakpoints.sm}) {
        min-height: 100vh;
        padding-top: 120px;
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        min-height: 100vh;
    }
`;

const Content = styled.div`
    display: flex;
    border-radius: 8px;
    min-height: calc(100% - 115px);
    padding: 0 20px 20px 20px;
    background: ${colors.neutralBackground};
    justify-content: space-between;
`;

const SubMenuToggle = styled.div`
    display: flex;
    align-items: center;
    padding: 4px 14px;
    border-radius: 10px;
    cursor: pointer;
    font-size: 14px;
    color: ${({ selected }) => selected ? colors.occasionalPurple : colors.nightDark};
    font-weight: ${({ selected }) => selected ? '600' : '500'};
    gap: 10px;
    transition: all 0.15s ease;
    &:hover { background: ${colors.night}08; }
`;

const SubMenuList = styled.div`
    display: flex;
    flex-direction: column;
    padding-left: 44px;
    gap: 2px;
    margin-bottom: 4px;
`;

const SubMenuItem = styled(Link).withConfig({
    shouldForwardProp: (prop) => !['selected'].includes(prop),
})`
    display: flex;
    align-items: center;
    padding: 8px 12px;
    border-radius: 8px;
    text-decoration: none;
    font-size: 14px;
    color: ${({ selected }) => selected ? colors.occasionalPurple : colors.night};
    font-weight: ${({ selected }) => selected ? '600' : '400'};
    background: ${({ selected }) => selected ? `${colors.occasionalPurple}15` : 'transparent'};
    &:hover { background: ${colors.neutralDark}; }
`;
